import { useCallback } from 'react';

import type { ActionSubjectIdType, SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useBoardId, useWorkspaceId } from '@trello/id-context';
import { useSharedState } from '@trello/shared-state';

import type { ViewName } from './boardMenuSharedState';
import { boardMenuSharedState } from './boardMenuSharedState';

const LEGACY_PANEL_NAMES = [
  'archive',
  'activity',
  'background',
  'backgroundColor',
  'backgroundPhoto',
  'collections',
];

export const useBoardMenuView = () => {
  const boardId = useBoardId();
  const workspaceId = useWorkspaceId();

  const [{ currentViewName, viewHistory }, setBoardMenuState] =
    useSharedState(boardMenuSharedState);

  const willRenderLegacyPanel = LEGACY_PANEL_NAMES.includes(currentViewName);

  const goToView = useCallback(
    (viewName: ViewName) => {
      setBoardMenuState({
        currentViewName: viewName,
        viewHistory: [...viewHistory, viewName],
        isMainView: viewName === 'default',
      });
    },
    [setBoardMenuState, viewHistory],
  );

  const goBack = useCallback(() => {
    if (viewHistory.length >= 1) {
      viewHistory.pop(); //remove the one we're on
      const newCurrentViewName = viewHistory.pop(); //get where we gotta go

      if (newCurrentViewName) {
        goToView(newCurrentViewName);
      } else {
        //this should never happen, but just in case!
        goToView('default');
      }
    }
  }, [viewHistory, goToView]);

  const resetView = useCallback(() => {
    setBoardMenuState({
      currentViewName: 'default',
      viewHistory: ['default'],
      isMainView: true,
    });
  }, [setBoardMenuState]);

  const sendClickedDrawerNavItemEvent = useCallback(
    (menuName: string, navItem: string, attributes: object) => {
      // Ensure that values are camel cased appropriately
      const menu = menuName[0].toUpperCase() + menuName.slice(1).toLowerCase();
      const nav = navItem[0].toLowerCase() + navItem.slice(1);

      const actionSubjectId =
        `${nav}DrawerNavigationItem` as ActionSubjectIdType;
      const source = `boardMenuDrawer${menu}Screen` as SourceType;

      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'drawerNavigationItem',
        actionSubjectId,
        source,
        containers: formatContainers({
          boardId,
          workspaceId,
        }),
        attributes: {
          ...attributes,
        },
      });
    },
    [boardId, workspaceId],
  );

  return {
    currentViewName,
    goBack,
    goToView,
    resetView,
    sendClickedDrawerNavItemEvent,
    viewHistory,
    willRenderLegacyPanel,
  };
};
